import React, { Component } from 'react'

import { ModalActionTypes } from '@acorns/web-components'
import { Type, read } from '@acorns/web-utils'
import { ApolloProvider } from '@apollo/react-hooks'
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
  defaultDataIdFromObject,
} from 'apollo-cache-inmemory'
import { persistCache } from 'apollo-cache-persist'
import ApolloClient from 'apollo-client'
import { ApolloLink, from } from 'apollo-link'
import { BatchHttpLink } from 'apollo-link-batch-http'
import { setContext } from 'apollo-link-context'
import DebounceLink from 'apollo-link-debounce'
import { onError } from 'apollo-link-error'
import { SessionRefresherProps, SessionTimer, withSessionTimer } from 'config/session'
import { path, pathOr } from 'ramda'
import { compose } from 'recompose'
import UAParser from 'ua-parser-js'

import {
  ACORNS_HELLO_FLOW,
  APP_ID,
  AUTH_TOKEN,
  PARTNER_PAYLOAD_STORAGE_KEY,
  USER_UUID,
} from 'app-constants'
import CenteredSpinner from 'components/CenteredSpinner'
import platform from 'components/Trackers/platform'
import introspectionQueryResultData from 'generated/fragmentTypes.json'
import mutationResolvers from 'graphql-local/resolvers/mutation'
import { spendPrefundAmountDefault } from 'graphql-local/resolvers/mutation/setSpendPrefundAmount'
import { signaturePointsDefaults } from 'graphql-local/resolvers/mutation/updateSignaturePoints'
import { typeDefs } from 'graphql-local/schema'
import {
  IErrorDetails,
  IErrorPayload,
  hasServiceUnavailableError,
  hasUnauthenticatedError,
  withApolloErrorModal,
} from 'utils/Apollo'
import Environment, { withEnv } from 'utils/environment'
import { sendToSentry } from 'utils/errors'
import { getFlow, getFunnelName } from 'utils/FlowProvider/utils'

const fragmentMatcher = new IntrospectionFragmentMatcher({
  introspectionQueryResultData,
})

type ProviderProps = {
  closeErrorModal: () => void
  env: Environment
  getCustomHandler: GetCustomHandlerType
  showErrorModal: (details: IErrorDetails) => void
} & SessionRefresherProps

type GetCustomHandlerType = (name: string) => (error: IErrorPayload) => IErrorDetails | boolean

export class ApolloProviderWrapper extends Component<
  ProviderProps,
  { loading: boolean; client: any }
> {
  constructor(props) {
    super(props)

    this.state = {
      loading: true,
      client: undefined,
    }

    this.resetSessionTimerOnActivity = this.resetSessionTimerOnActivity.bind(this)
    this.resetSessionTimerState = this.resetSessionTimerState.bind(this)

    this.createApolloClient()
      .then(client => this.setState({ client, loading: false }))
      .catch(error =>
        sendToSentry(error, {
          name: 'createApolloClient() ERROR',
          message:
            'App will not render because Apollo Client is not instantiated. This is a possible SEV-1',
        }),
      )
  }

  public render() {
    return this.state.loading ? (
      <CenteredSpinner />
    ) : (
      <ApolloProvider client={this.state.client}>
        <SessionTimer
          shouldReset={this.props.shouldReset}
          afterReset={this.resetSessionTimerState}
          modalFactory={this.props.showErrorModal}
          closeModal={this.props.closeErrorModal}
        />
        {this.props.children}
      </ApolloProvider>
    )
  }

  protected async createApolloClient() {
    // Construct cache configuration
    const cache = new InMemoryCache({
      dataIdFromObject: (object: any) => {
        return defaultDataIdFromObject(object)
      },
      fragmentMatcher,
    })

    // Define default state.  Initially, we will use this only for the Later
    // Later Suitability flow, but this can be extended to any place where we
    // want to store client-side state
    cache.writeData({
      data: {
        storedQuestions: [],
        recommendation: null,
        coreSuitabilityQuestions: [],
        showCoreSuitabilityQuestions: false,
        page: 1,
        showNextButton: false,
        existingCoreCustomer: false,
        ...signaturePointsDefaults,
        ...spendPrefundAmountDefault,
      },
    })

    try {
      await persistCache({
        cache,
        storage: window.sessionStorage,
      })
    } catch (error) {
      sendToSentry(error, {
        name: 'Apollo Persist Cache Error',
        message: 'Attempted to setup Apollo Client with persistCache()',
      })
    }

    // Note: All GraphQL and network errors get run through this
    // so we can take appropriate action
    const errorLink = this.createErrorLink()

    const networkCallbackLink = this.createNetworkCallback(
      /* Tell session timer that there is user activity. Session timer is responsible
       * for deciding when to refresh the session based on that knowledge. */
      this.resetSessionTimerOnActivity,
    )

    // Note: Batch queries executed within an interval together
    // in a single network request
    const batchHttpLink = new BatchHttpLink({
      uri: this.props.env.get('graphql'),
      credentials: 'include',
    })

    const headers = this.setHeaders()

    const setAuthorizationLink = setContext(() => {
      const token = read(Type.token) || window.sessionStorage.getItem(AUTH_TOKEN)

      return {
        headers: { ...headers, authorization: `token ${token}` },
      }
    })

    // Note: Performs debounce logic on queries that have a `debounceKey` configured
    const DEBOUNCE_TIMEOUT = 250
    const debounceLink = new DebounceLink(DEBOUNCE_TIMEOUT) as any

    const links = [
      errorLink,
      debounceLink,
      networkCallbackLink,
      setAuthorizationLink,
      batchHttpLink,
    ]

    return new ApolloClient({
      cache,
      link: from(links),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-first',
        },
      },
      typeDefs,
      resolvers: {
        Mutation: mutationResolvers,
      },
    })
  }

  protected getReleaseTag(): string {
    if (!this.props.env) {
      return process.env.HEAD.substring(0, 7) || ''
    } else {
      return this.props.env.get('release') || this.props.env.get('head').substring(0, 7)
    }
  }

  protected setHeaders() {
    const userAgentParser = new UAParser()
    const userAgent = userAgentParser.getResult()
    const release = this.getReleaseTag()

    return {
      Accept: 'application/json, text/plain, */*',
      'X-Client-App': APP_ID,
      'X-Client-Browser': userAgent.browser.name,
      'X-Client-Browser-Version': userAgent.browser.version,
      'X-Client-Build': release,
      'X-Client-Hardware': userAgent.device.model,
      'X-Client-Os': userAgent.os.name,
      'X-Client-Platform': 'web',
    }
  }

  protected trackError(errorContext: object) {
    return window.analytics.track('Funnel Error', {
      event: 'funnel_error',
      event_text: 'Funnel Error',
      platform,
      funnel: getFunnelName(getFlow()),
      path: window.location.pathname,
      ...errorContext,
      partner: pathOr(
        null,
        ['org', 'name'],
        JSON.parse(window.sessionStorage.getItem(PARTNER_PAYLOAD_STORAGE_KEY)),
      ),
    })
  }

  protected createNetworkCallback(cb: () => boolean) {
    return new ApolloLink((operation, forward) => {
      if (cb()) {
        cb()
        return forward(operation)
      }

      return null
    })
  }

  protected resetSessionTimerOnActivity() {
    this.props.setShouldReset(true)
    return true
  }

  // Callback to reset the trigger for refreshing session cookie
  protected resetSessionTimerState() {
    this.props.setShouldReset(false)
  }

  /* istanbul ignore next */
  protected createErrorLink() {
    return onError(({ graphQLErrors, networkError, operation }) => {
      // Determine the type of error and handle accordingly
      if (
        operation.operationName !== 'IsAuthenticated' &&
        operation.operationName !== 'RegistrationCheck' &&
        window.localStorage.getItem(USER_UUID) !== null &&
        hasUnauthenticatedError(graphQLErrors)
      ) {
        if (
          getFlow() === 'laterOnboarding' ||
          getFlow() === 'directToMobile' ||
          getFlow() === 'mightyOakCardOnboarding'
        ) {
          // The user is supposed to be already authenticated in the laterOnboarding flow.
          // If the user is not, then we remove the flow flag and send them back to the beginning.
          window.localStorage.removeItem(ACORNS_HELLO_FLOW)
        }
        this.handleAuthenticationError()
      } else if (hasServiceUnavailableError(graphQLErrors)) {
        this.handleServiceUnavailableError()
      } else {
        this.handleError(graphQLErrors, networkError, operation)
      }
    })
  }

  protected handleAuthenticationError() {
    // TODO:  Determine the authentication and timeout requirements
    // for web-registration.  For now, just close the Modal.
    const errorModalHandler = () => {
      this.props.closeErrorModal()
      window.location.assign('/')
    }

    this.props.showErrorModal({
      title: 'Unauthenticated',
      message: 'Your request could not be processed because you are not authenticated.',
      actions: [
        {
          handler: errorModalHandler,
          label: 'Ok',
          type: ModalActionTypes.primary,
        },
      ],
    })
    this.trackError({
      error_message: 'Your request could not be processed because you are not authenticated.',
    })
  }

  protected handleServiceUnavailableError() {
    this.props.showErrorModal({
      title: 'Please Contact Support',
      message:
        'We are unable to process your request online, but our support team is here to help. Please contact us and we will work to resolve this as soon as possible.',
      actions: [
        {
          handler: this.props.closeErrorModal,
          label: 'Cancel',
          type: ModalActionTypes.secondary,
        },
        {
          href: 'https://www.acorns.com/support/contact/',
          label: 'Contact Support',
          type: ModalActionTypes.primary,
        },
      ],
    })
    this.trackError({
      error_message:
        'We are unable to process your request online, but our support team is here to help. Please contact us and we will work to resolve this as soon as possible.',
    })
  }

  protected handleError(graphQLErrors, networkError, operation) {
    if (graphQLErrors) {
      const payload = graphQLErrors?.[0]?.data ??
        graphQLErrors?.[0]?.extensions?.response ?? {
          operation: JSON.stringify(operation),
          networkError: JSON.stringify(networkError),
          error: JSON.stringify(graphQLErrors),
        }
      this.trackError(payload)
    }
    const operationType = path(['query', 'definitions', '0', 'operation'], operation)
    const handler = this.props.getCustomHandler(operation.operationName)

    let errorDetails

    if (typeof handler === 'function') {
      errorDetails = handler({
        graphQLErrors,
        networkError,
      })
    }

    Promise.resolve(errorDetails).then(results => {
      // Disable this error modal if the handler returns false
      if (typeof results === 'boolean' && !results) {
        return
      }

      if (operationType === 'mutation' || results) {
        this.props.showErrorModal(results)
      }
    })
  }
}

export const enhance = compose(withApolloErrorModal, withSessionTimer, withEnv)

export const Provider = enhance(ApolloProviderWrapper)
